import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import GenericModal from '../../../components/Modals/genericModal';

// A long list gets a filter, so one file can be found without scrolling.
const FILTER_FROM = 15;

export const filesText = (count) => `${count} logic ${count === 1 ? 'file' : 'files'}`;

const linkButton = 'text-sm font-medium text-indigo-600 hover:underline disabled:opacity-50 disabled:no-underline dark:text-brand-blue';

/**
 * Lets the person choose which logic files an action covers: the export, or the import of an export file or a backup.
 * `onConfirm` gets the chosen names, or null when every file is chosen, which is the action as it was before there was
 * a choice. `initiallySelected` is the names to start from, for a dialog that is opened again to change an earlier choice.
 * `idPrefix` keeps the element ids of each use apart: "export" for the export.
 */
export default function ChooseLogicFilesModal({
  open, onClose, onConfirm, files, idPrefix, title, intro, emptyText, confirmText, busyText, progressText, errorText, successToast, initiallySelected,
}) {
  const names = useMemo(
    () => [...new Set((files || []).map(f => f?.fileName).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [files]);
  const startFrom = () => new Set(initiallySelected ? names.filter(n => initiallySelected.includes(n)) : names);
  const [selected, setSelected] = useState(startFrom);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Every time the dialog opens it starts from the earlier choice, or from "all selected" the first time.
  useEffect(() => {
    if (open) {
      setSelected(startFrom());
      setQuery('');
      setError(null);
    }
  }, [open, names]);

  const needle = query.trim().toLowerCase();
  const shown = needle ? names.filter(n => n.toLowerCase().includes(needle)) : names;
  const count = selected.size;

  const toggle = (name) => setSelected(current => {
    const next = new Set(current);
    if (!next.delete(name))
      next.add(name);
    return next;
  });

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(count === names.length ? null : names.filter(n => selected.has(n)));
      if (successToast)
        toast.success(successToast);
      onClose();
    } catch {
      setError(errorText);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (!busy)
      onClose();
  };

  const confirmLabel = busy ? busyText : error ? 'Try again' : confirmText(count);

  return (
    <GenericModal
      open={open}
      setOpen={close}
      title={title}
      showCancelButton
      onCancel={close}
      cancelButtonDisabled={busy}
      confirmButtonText={confirmLabel}
      confirmButtonDisabled={busy || count === 0}
      onConfirm={confirm}
    >
      <div id={`${idPrefix}-connections-modal`} className="mt-4 text-left text-sm text-gray-700 dark:text-gray-300" data-state={busy ? 'busy' : error ? 'error' : 'idle'}>
        <p id={`${idPrefix}-connections-intro`}>{intro}</p>

        {names.length === 0 && <p className="mt-3">{emptyText}</p>}

        {names.length > 0 && (
          <>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <span id={`${idPrefix}-connections-count`} aria-live="polite">{`${count} of ${names.length} selected`}</span>
              <span className="flex items-center gap-2">
                <button id={`${idPrefix}-select-all`} type="button" className={linkButton} disabled={busy || count === names.length}
                  onClick={() => setSelected(new Set(names))}>Select all</button>
                <span aria-hidden="true">·</span>
                <button id={`${idPrefix}-select-none`} type="button" className={linkButton} disabled={busy || count === 0}
                  onClick={() => setSelected(new Set())}>Select none</button>
              </span>
            </div>

            {names.length > FILTER_FROM && (
              <div className="mt-3">
                <label htmlFor={`${idPrefix}-filter`} className="sr-only">Filter logic files</label>
                <input id={`${idPrefix}-filter`} type="search" value={query} placeholder="Logic file" disabled={busy} onChange={e => setQuery(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              </div>
            )}

            <fieldset className="mt-3 max-h-80 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700" disabled={busy}>
              <legend className="sr-only">Logic files</legend>
              <ul>
                {shown.map((name, idx) => (
                  <li key={name} className="px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <label htmlFor={`${idPrefix}-file-${idx}`} className="flex items-center gap-2 break-all text-gray-900 dark:text-gray-200">
                      <input id={`${idPrefix}-file-${idx}`} type="checkbox" checked={selected.has(name)} onChange={() => toggle(name)}
                        className="h-4 w-4 flex-none rounded border-gray-300 text-indigo-600" />
                      {name}
                    </label>
                  </li>
                ))}
                {shown.length === 0 && <li className="px-3 py-2">No logic file matches the filter.</li>}
              </ul>
            </fieldset>
          </>
        )}

        {count === 0 && names.length > 0 && <p id={`${idPrefix}-connections-none`} className="mt-2 text-yellow-800 dark:text-yellow-200">Choose at least one logic file.</p>}

        {busy && (
          <p id={`${idPrefix}-connections-progress`} role="status" className="mt-4 flex items-center gap-2">
            <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
            {progressText(count)}
          </p>
        )}
        {error && !busy && (
          <div id={`${idPrefix}-connections-error`} role="alert" className="mt-4 rounded-md bg-red-50 p-3 text-red-800 dark:bg-red-900 dark:text-red-100">{error}</div>
        )}
      </div>
    </GenericModal>
  );
}
